import {
  Alert,
  Badge,
  Button,
  Field,
  Modal,
  PageHeader,
  Radio,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Receipt } from 'lucide-react';
import { type JSX, useState } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  BILLING_CONCEPT_TYPE_LABELS,
  BILLING_CONCEPT_TYPES,
  type BillingConcept,
  type BillingConceptType,
  createBillingConcept,
  listBillingConcepts,
  updateBillingConcept,
} from '../../invoices/billing-concepts-api';
import { listVatRates } from '../../invoices/vat-rates-api';

type ModalState = { mode: 'create' } | { mode: 'edit'; concept: BillingConcept } | null;

/** Codes from `BillingExceptionFilter` (`apps/api` `billing-exception.filter.ts`). */
function mapBillingConceptError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === 'BILLING_CONCEPT_NAME_DUPLICATE') {
    return 'Ya existe un concepto con ese nombre.';
  }

  if (error.status === 409 && code === 'BILLING_CONCEPT_DEFAULT_PROTECTED') {
    return 'El concepto por defecto no se puede desactivar.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 422) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * `billing-concepts/spec.md` — "Pantalla de configuración de conceptos de
 * facturación". The hub card is gated on `fiscal.config`; the page
 * double-checks it (same reasoning as `SystemParametersPage`) because a
 * direct URL must not render the table.
 */
export function BillingConceptConfigPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modal, setModal] = useState<ModalState>(null);
  const [name, setName] = useState('');
  const [arcaType, setArcaType] = useState<BillingConceptType | null>(null);
  const [defaultDescription, setDefaultDescription] = useState('');
  const [defaultVatRateId, setDefaultVatRateId] = useState('');

  const canConfigure = hasPermission(currentUser.data, 'fiscal.config');
  const query = useQuery({
    queryKey: ['billing-concepts'],
    queryFn: listBillingConcepts,
    enabled: canConfigure,
  });

  const vatRatesQuery = useQuery({
    queryKey: ['vat-rates'],
    queryFn: listVatRates,
    enabled: canConfigure,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (modal === null || arcaType === null) {
        return;
      }

      const trimmedDescription = defaultDescription.trim();
      const input = {
        name: name.trim(),
        arcaType,
        defaultDescription: trimmedDescription === '' ? null : trimmedDescription,
      };

      if (modal.mode === 'create') {
        await createBillingConcept(
          defaultVatRateId === '' ? input : { ...input, defaultVatRateId },
        );
      } else {
        await updateBillingConcept(modal.concept.id, {
          ...input,
          defaultVatRateId: defaultVatRateId === '' ? null : defaultVatRateId,
        });
      }
    },
    onSuccess: async () => {
      toast('success', 'Guardado');
      setModal(null);
      await queryClient.invalidateQueries({ queryKey: ['billing-concepts'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateBillingConcept(id, { isActive }),
    onSuccess: async () => {
      toast('success', 'Guardado');
      await queryClient.invalidateQueries({ queryKey: ['billing-concepts'] });
    },
  });

  function openModal(state: ModalState): void {
    saveMutation.reset();
    setName(state?.mode === 'edit' ? state.concept.name : '');
    setArcaType(state?.mode === 'edit' ? state.concept.arcaType : null);
    setDefaultDescription(state?.mode === 'edit' ? (state.concept.defaultDescription ?? '') : '');
    setDefaultVatRateId(state?.mode === 'edit' ? (state.concept.defaultVatRateId ?? '') : '');
    setModal(state);
  }

  if (currentUser.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }

  if (!canConfigure) {
    return (
      <Alert tone="danger" title="Sección restringida">
        No tenés permiso para acceder a los conceptos de facturación.
      </Alert>
    );
  }

  const concepts = query.data ?? [];

  // Only active rates can be assigned; a rate deactivated after being chosen stays listed so editing does not silently drop it.
  const vatRateOptions = (vatRatesQuery.data ?? [])
    .filter((item) => item.isActive || item.id === defaultVatRateId)
    .map((item) => ({ id: item.id, name: item.name }));

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Receipt}
        title="Conceptos de facturación"
        description="Definí los conceptos que se asignan a los precios y viajan en cada factura."
        actions={
          <Button icon={Plus} onClick={() => openModal({ mode: 'create' })}>
            Nuevo concepto de facturación
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los conceptos de facturación">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {toggleActiveMutation.isError ? (
        <Alert tone="danger">{mapBillingConceptError(toggleActiveMutation.error)}</Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <Table>
          <caption className="sr-only">Conceptos de facturación</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Tipo ARCA</TableHeader>
              <TableHeader>Descripción por defecto</TableHeader>
              <TableHeader>Alícuota por defecto</TableHeader>
              <TableHeader>Precios que lo usan</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {concepts.map((concept) => (
              <TableRow key={concept.id}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    {concept.name}
                    {concept.isSystemDefault ? <Badge variant="info">Por defecto</Badge> : null}
                  </span>
                </TableCell>
                <TableCell>{BILLING_CONCEPT_TYPE_LABELS[concept.arcaType]}</TableCell>
                <TableCell>{concept.defaultDescription ?? '—'}</TableCell>
                <TableCell>
                  {concept.defaultVatRate?.name ?? '—'}
                </TableCell>
                <TableCell>{concept.priceUsageCount}</TableCell>
                <TableCell>
                  <Badge variant={concept.isActive ? 'success' : 'neutral'}>
                    {concept.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Pencil}
                      aria-label={`Editar ${concept.name}`}
                      onClick={() => openModal({ mode: 'edit', concept })}
                    >
                      Editar
                    </Button>
                    {concept.isSystemDefault ? null : (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={
                          toggleActiveMutation.isPending &&
                          toggleActiveMutation.variables?.id === concept.id
                        }
                        onClick={() =>
                          toggleActiveMutation.mutate({
                            id: concept.id,
                            isActive: !concept.isActive,
                          })
                        }
                      >
                        {concept.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {modal !== null ? (
        <Modal
          open
          onClose={() => setModal(null)}
          title={modal.mode === 'create' ? 'Nuevo concepto de facturación' : 'Editar concepto'}
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {saveMutation.isError ? (
              <Alert tone="danger">{mapBillingConceptError(saveMutation.error)}</Alert>
            ) : null}

            <Field
              label="Nombre"
              name="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-slate-900">Tipo ARCA</legend>
              {BILLING_CONCEPT_TYPES.map((type) => (
                <Radio
                  key={type}
                  label={BILLING_CONCEPT_TYPE_LABELS[type]}
                  name="arcaType"
                  checked={arcaType === type}
                  onChange={() => setArcaType(type)}
                />
              ))}
            </fieldset>

            <Field
              label="Descripción por defecto"
              name="defaultDescription"
              value={defaultDescription}
              onChange={(event) => setDefaultDescription(event.target.value)}
            />

            <div className="flex flex-col gap-1.5">
              <label htmlFor="default-vat-rate" className="text-sm font-medium text-slate-900">
                Alícuota de IVA por defecto
              </label>
              {vatRatesQuery.isError ? (
                <Alert tone="danger">
                  <div className="flex flex-wrap items-center gap-3">
                    <span>No pudimos cargar las alícuotas de IVA.</span>
                    <Button variant="secondary" onClick={() => void vatRatesQuery.refetch()}>
                      Reintentar
                    </Button>
                  </div>
                </Alert>
              ) : null}
              <select
                id="default-vat-rate"
                value={defaultVatRateId}
                onChange={(event) => setDefaultVatRateId(event.target.value)}
                className="h-11 rounded-md border border-slate-300 px-3 text-sm"
              >
                <option value="">Sin alícuota por defecto</option>
                {vatRateOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                Se propone al cargar precios con este concepto; no modifica los precios existentes.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                loading={saveMutation.isPending}
                disabled={name.trim() === '' || arcaType === null}
                onClick={() => saveMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
